import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { ApiProvider } from './contexts/ApiContext';
import { AuthProvider } from './contexts/AuthContext';
import './App.css';
import { AppRoutes } from './config/routes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: 2,
      refetchOnWindowFocus: true,
    },
  },
});

/**
 * Main App Component with Advanced Routing Architecture
 * 
 * Features:
 * - Role-based routing with protected routes
 * - Multiple layout support (Default, Auth, Dashboard)
 * - Comprehensive error boundaries
 * - Type-safe routing configuration
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ApiProvider>
        <AuthProvider>
          {/* Mobile Not Supported - CSS-based fallback */}
          <div className="block md:hidden">
            <div className="min-h-screen bg-gradient-to-br from-[#0B0B0F] to-[#713F12] flex items-center justify-center p-4">
              <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
                {/* Icon */}
                <div className="mb-6">
                  <div className="mx-auto w-20 h-20 bg-gradient-to-br from-[#EAB308] to-[#713F12] rounded-full flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>

                {/* Title */}
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                  Desktop Required
                </h1>

                {/* Message */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                  This dashboard is optimized for desktop and tablet devices. 
                  For the best experience, please access it from a computer or tablet with a larger screen.
                </p>

                {/* Features List */}
                <div className="text-left mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    Why desktop is recommended:
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-[#EAB308] mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Better data visualization
                    </li>
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-[#EAB308] mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Enhanced navigation experience
                    </li>
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-[#EAB308] mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Full feature access
                    </li>
                    <li className="flex items-center">
                      <svg className="w-4 h-4 text-[#EAB308] mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Optimal performance
                    </li>
                  </ul>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-gradient-to-r from-[#EAB308] to-[#CA8A04] text-[#0B0B0F] font-semibold py-3 px-6 rounded-lg hover:from-[#CA8A04] hover:to-[#A16207] transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Try Again
                </button>

                {/* Footer */}
                <p className="text-xs text-gray-500 mt-4">
                  Minimum screen width: 1024px recommended
                </p>
              </div>
            </div>
          </div>

          {/* Desktop Dashboard - Hidden on mobile */}
          <div className="hidden md:block">
            <AppRoutes />
          </div>

          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                style: {
                  background: '#EAB308',
                },
              },
              error: {
                duration: 5000,
                style: {
                  background: '#EF4444',
                },
              },
            }}
          />
        </AuthProvider>
      </ApiProvider>
    </BrowserRouter>
    <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;